import useSchema from "hooks/useSchema";
import { useDispatch } from "react-redux";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import * as config from "util/config";
import { transferTypes } from "util/transfer";
import * as Yup from "yup";

const MIN_REQUIRED = config.getInteger("cbuSearch.min.required", 6);
const MAX_REQUIRED = "22";

export const useOtherValidations = (accounts) => {
    const dispatch = useDispatch();
    const { stringRequired, numberRequired, emailRequired, stringNullableRequired } = useSchema();

    const isValidCbu = (value) => {
        const reg = /^\d+$/;
        return reg.test(value) && value.length === parseInt(MAX_REQUIRED, 10);
    };

    const isValidAlias = (value) => {
        /* Regex that accept letter from a to z including capital letters, (.), (-), numbers
        and excluding (Ñ) and (ñ) */
        const reg = /^(?:(?![ñÑ])[a-zA-Z.\-\d])+$/;
        return reg.test(value);
    };

    const validateCbuOrAlias = (value) => isValidCbu(value) || isValidAlias(value);

    const otherTransferSchema = Yup.object().shape({
        debitAccount: stringRequired,
        transferKind: stringRequired,
        creditCbu: Yup.string()
            .min(parseInt(MIN_REQUIRED, 10), `${i18n.get("forms.validation.min.required")} ${MIN_REQUIRED}`)
            .max(parseInt(MAX_REQUIRED, 10), `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED}`)
            .required(i18n.get(`global.error.required`))
            .test("format", i18n.get(`global.incorrect.format`), validateCbuOrAlias),
        amountField: Yup.object()
            .shape({
                amount: numberRequired,
            })
            .test(
                "availableAmount",
                i18n.get("transfer.amount.error"), // eslint-disable-next-line func-names
                function(value) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    const { debitAccount = "" } = this.parent;
                    const selected = accounts.find((_account) => _account.productId === debitAccount);
                    if (value.amount > selected?.availableBalance) {
                        dispatch(
                            notificationActions.showNotification("transfers.insuficient.amount.error", "error", [
                                "otherTransfers",
                            ]),
                        );
                    }
                    return value.amount <= selected?.availableBalance;
                },
            ),
        reasonCode: stringNullableRequired,
        email: Yup.string().when("sendEmail", (sendEmail, schema) => (sendEmail ? emailRequired : schema)),
    });

    const validateAccounts = (transferKind, currency, beneficiary) => {
        const isDiferentTransferKind = transferKind !== transferTypes[beneficiary.isOwn];
        const isDiferentCurrency = currency !== beneficiary.productType.split(" ")[1];

        if (isDiferentTransferKind) {
            dispatch(
                notificationActions.showNotification(i18n.get("transfers.validation.transferKind"), "error", [
                    "otherTransfers",
                ]),
            );
            return false;
        }

        if (isDiferentCurrency) {
            dispatch(
                notificationActions.showNotification(i18n.get("transfers.validation.currency"), "error", [
                    "otherTransfers",
                ]),
            );
            return false;
        }
        return true;
    };

    const validateMepAccounts = (transferKind, currency, beneficiary) => {
        const isDiferentTransferKind = transferKind !== transferTypes[beneficiary.isOwn];
        const isDiferentCurrency = currency !== beneficiary.productType.split(" ")[1];

        if (isDiferentTransferKind) {
            dispatch(
                notificationActions.showNotification(i18n.get("transfers.validation.transferKind"), "error", [
                    "mepNotification",
                ]),
            );
            return false;
        }

        if (isDiferentCurrency) {
            dispatch(
                notificationActions.showNotification(i18n.get("transfers.validation.currency"), "error", [
                    "mepNotification",
                ]),
            );
            return false;
        }
        return true;
    };

    return { otherTransferSchema, validateAccounts, validateMepAccounts };
};

export default useOtherValidations;
